/** Tiny DOM helpers — no framework. */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Record<string, string | boolean | undefined | null> | null,
  ...children: (Node | string | null | undefined | false)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props) {
    for (const [k, v] of Object.entries(props)) {
      if (v === undefined || v === null || v === false) continue;
      if (k === "className") {
        node.className = String(v);
      } else if (k === "textContent") {
        node.textContent = String(v);
      } else if (k.startsWith("on") && typeof v === "string") {
        // not used — prefer addEventListener
      } else if (v === true) {
        node.setAttribute(k, "");
      } else {
        node.setAttribute(k, String(v));
      }
    }
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    node.append(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

export function clear(node: HTMLElement): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function formatDate(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  try {
    return new Date(t).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

/**
 * Wrap a password input with a Show/Hide toggle.
 * Returns a container that replaces the bare input inside labels.
 * The original input element is kept so callers can still read `.value`.
 */
export function withShowPassword(input: HTMLInputElement): HTMLElement {
  // Ensure we start hidden (password type).
  if (input.type !== "password" && input.type !== "text") {
    input.type = "password";
  }
  input.classList.add("password-input");

  const wrap = el("div", { className: "password-field" });
  const toggle = el(
    "button",
    {
      type: "button",
      className: "password-toggle",
      "aria-label": "Show password",
      "aria-pressed": "false",
      tabindex: "0",
    },
    "Show",
  ) as HTMLButtonElement;

  const setVisible = (visible: boolean) => {
    input.type = visible ? "text" : "password";
    toggle.textContent = visible ? "Hide" : "Show";
    toggle.setAttribute("aria-label", visible ? "Hide password" : "Show password");
    toggle.setAttribute("aria-pressed", visible ? "true" : "false");
  };

  toggle.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setVisible(input.type === "password");
  });

  wrap.append(input, toggle);
  return wrap;
}
